(() => {
  const modalSelector = '.kiwi-sizing-modal';
  const messageSelector = `${modalSelector} .ks-html-content`;
  const tableSelector = '.ks-table';
  const cellSelector = '.ks-table-cell';
  const emptyClass = 'cw-kiwi-message-empty';
  const fixedAttribute = 'data-cw-column-hover-fix';

  const setImportant = (element, property, value) => {
    if (!element) return;

    const currentValue = element.style.getPropertyValue(property);
    const currentPriority = element.style.getPropertyPriority(property);

    if (currentValue === value && currentPriority === 'important') return;
    element.style.setProperty(property, value, 'important');
  };

  const updateMessage = (message) => {
    const text = (message.innerText || message.textContent || '')
      .replace(/[\u00a0\u200B-\u200D\u2060\uFEFF]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const hasReadableContent = /[\p{L}\p{N}]/u.test(text);

    message.classList.toggle(emptyClass, !hasReadableContent);

    if (hasReadableContent) {
      message.style.removeProperty('display');
      message.removeAttribute('aria-hidden');
    } else {
      message.style.setProperty('display', 'none', 'important');
      message.setAttribute('aria-hidden', 'true');
    }
  };

  const normalizeTable = (table, isMobile = false) => {
    setImportant(table, 'width', '100%');
    setImportant(table, 'height', 'auto');
    setImportant(table, 'min-height', '0');
    setImportant(table, 'max-height', 'none');
    setImportant(table, 'table-layout', 'fixed');
    setImportant(table, 'font-family', 'Inter, Arial, sans-serif');

    const cells = table.querySelectorAll(
      '.ks-table-cell, .ks-table-row > *, tr > th, tr > td'
    );

    cells.forEach((cell) => {
      setImportant(cell, 'width', '25%');
      setImportant(cell, 'min-width', '25%');
      setImportant(cell, 'max-width', '25%');
      setImportant(cell, 'flex', '0 0 25%');
      setImportant(cell, 'box-sizing', 'border-box');
      setImportant(cell, 'font-family', 'Inter, Arial, sans-serif');
      setImportant(cell, 'font-style', 'normal');
      setImportant(cell, 'text-align', 'center');

      if (isMobile) {
        setImportant(cell, 'height', '100%');
        setImportant(cell, 'min-height', '0');
        setImportant(cell, 'max-height', 'none');
        setImportant(cell, 'padding', '0 4px');
        setImportant(cell, 'border-top', '0');
        setImportant(cell, 'border-left', '0');
        setImportant(cell, 'border-right', '1px solid rgba(255, 255, 255, 0.065)');
        setImportant(cell, 'border-bottom', '1px solid rgba(255, 255, 255, 0.065)');
        setImportant(cell, 'outline', '0');
        setImportant(cell, 'box-shadow', 'none');
      }
    });

    if (isMobile) {
      table.querySelectorAll('.ks-table-row, tr').forEach((row) => {
        setImportant(row, 'border', '0');
        setImportant(row, 'outline', '0');
        setImportant(row, 'box-shadow', 'none');

        const rowCells = row.querySelectorAll('.ks-table-cell, th, td, :scope > *');
        rowCells.forEach((cell, index) => {
          if (index === rowCells.length - 1) setImportant(cell, 'border-right', '0');
        });
      });

      const rows = table.querySelectorAll('.ks-table-row, tr');
      const lastRow = rows[rows.length - 1];
      lastRow?.querySelectorAll('.ks-table-cell, th, td, :scope > *').forEach((cell) => {
        setImportant(cell, 'border-bottom', '0');
      });
    }

    const firstRow = table.querySelector('.ks-table-row, tr');
    const firstCell = firstRow?.querySelector('.ks-table-cell, th, td, :scope > *');

    if (firstCell) {
      setImportant(firstCell, 'padding-left', '0');
      setImportant(firstCell, 'padding-right', '0');
      setImportant(firstCell, 'text-align', 'center');
      firstCell.querySelectorAll('*').forEach((node) => {
        setImportant(node, 'text-align', 'center');
      });
    }
  };

  const normalizeModal = (modal) => {
    const isMobile = window.matchMedia('(max-width: 599px)').matches;
    const inner = modal.querySelector('.kiwi-sizing-modal-inner');
    const content = modal.querySelector('.ks-modal-content');
    const message = modal.querySelector('.ks-html-content');
    const wrapper = modal.querySelector('.ks-table-wrapper');
    const table = modal.querySelector(tableSelector);

    // Safari de iOS: un fixed con inset 0 no cubre las zonas de sus barras (arriba y abajo asomaba la página en
    // blanco). El fondo sobresale 120px por cada lado y la hoja se prolonga hacia abajo con su mismo color.
    setImportant(modal, 'top', isMobile ? '-120px' : '0');
    setImportant(modal, 'bottom', isMobile ? '-120px' : '0');
    if (inner && inner.dataset.cwSombraOriginal === undefined) inner.dataset.cwSombraOriginal = getComputedStyle(inner).boxShadow;
    if (inner) {
      const sombraOriginal = inner.dataset.cwSombraOriginal && inner.dataset.cwSombraOriginal !== 'none' ? inner.dataset.cwSombraOriginal : '';
      setImportant(inner, 'box-shadow', isMobile ? [sombraOriginal, '0 160px 0 0 rgb(31, 31, 36)'].filter(Boolean).join(', ') : sombraOriginal || 'none');
      setImportant(inner, 'position', 'fixed');
      setImportant(inner, 'width', isMobile ? '100%' : 'min(720px, calc(100vw - 56px))');
      setImportant(inner, 'max-width', isMobile ? 'none' : 'min(720px, calc(100vw - 56px))');
      // Móvil: hoja inferior que se ajusta a su contenido (tope en la pantalla), nunca forzada a pantalla completa:
      // con una tabla corta, la altura completa dejaba debajo un hueco vacío (reportado en iPhone, 22-sep-2026).
      setImportant(inner, 'height', 'auto');
      setImportant(inner, 'min-height', '0');
      setImportant(inner, 'max-height', isMobile ? 'calc(100dvh - 8px)' : (window.innerHeight <= 640 ? 'calc(100dvh - 20px)' : 'none'));
      setImportant(inner, 'overflow', 'hidden');
      setImportant(inner, 'box-sizing', 'border-box');
      setImportant(inner, 'margin', '0');

      if (isMobile) {
        setImportant(inner, 'top', 'auto');
        setImportant(inner, 'left', '0');
        setImportant(inner, 'right', '0');
        // El fondo (con backdrop-filter) es el bloque contenedor de la hoja y sobresale 120px: se compensa para
        // que la hoja apoye justo en el borde inferior de la pantalla.
        setImportant(inner, 'bottom', '120px');
        setImportant(inner, 'transform', 'none');
        setImportant(inner, 'display', 'flex');
        setImportant(inner, 'flex-direction', 'column');
      } else {
        setImportant(inner, 'top', '50%');
        setImportant(inner, 'left', '50%');
        setImportant(inner, 'right', 'auto');
        setImportant(inner, 'bottom', 'auto');
        setImportant(inner, 'transform', 'translate(-50%, -50%)');
        setImportant(inner, 'display', 'block');
      }
    }

    if (content) {
      setImportant(content, 'width', '100%');
      setImportant(content, 'height', 'auto');
      setImportant(content, 'min-height', '0');
      setImportant(content, 'max-height', isMobile ? 'none' : (window.innerHeight <= 640 ? 'calc(100dvh - 110px)' : 'none'));
      setImportant(content, 'overflow-x', 'hidden');
      setImportant(content, 'overflow-y', isMobile ? 'auto' : (window.innerHeight <= 640 ? 'auto' : 'hidden'));
      setImportant(content, 'background', 'transparent');
      setImportant(content, 'box-sizing', 'border-box');
      setImportant(content, 'font-family', 'Inter, Arial, sans-serif');
      setImportant(content, 'overscroll-behavior', 'contain');
      setImportant(content, '-webkit-overflow-scrolling', 'touch');
      setImportant(content, 'flex', '0 1 auto');

      if (isMobile) {
        setImportant(content, 'padding', '15px 12px max(10px, env(safe-area-inset-bottom))');
      } else {
        setImportant(content, 'padding', '26px 30px 14px');
      }
    }

    if (message) {
      const isEmptyMessage = message.classList.contains(emptyClass);

      if (isEmptyMessage) {
        setImportant(message, 'display', 'none');
        setImportant(message, 'width', '0');
        setImportant(message, 'height', '0');
        setImportant(message, 'min-width', '0');
        setImportant(message, 'min-height', '0');
        setImportant(message, 'max-width', '0');
        setImportant(message, 'max-height', '0');
        setImportant(message, 'margin', '0');
        setImportant(message, 'padding', '0');
        setImportant(message, 'overflow', 'hidden');
        setImportant(message, 'border', '0');
        setImportant(message, 'box-shadow', 'none');
        setImportant(message, 'opacity', '0');
        setImportant(message, 'visibility', 'hidden');
      } else {
        setImportant(message, 'display', 'flex');
        setImportant(message, 'width', '100%');
        setImportant(message, 'height', isMobile ? '40px' : '44px');
        setImportant(message, 'min-width', '0');
        setImportant(message, 'min-height', isMobile ? '40px' : '44px');
        setImportant(message, 'max-width', 'none');
        setImportant(message, 'max-height', isMobile ? '40px' : '44px');
        setImportant(message, 'font-family', 'Inter, Arial, sans-serif');
        setImportant(message, 'font-style', 'normal');
        setImportant(message, 'font-weight', '600');
        setImportant(message, 'align-items', 'center');
        setImportant(message, 'justify-content', 'flex-start');
        setImportant(message, 'text-align', 'left');
        setImportant(message, 'opacity', '1');
        setImportant(message, 'visibility', 'visible');

        if (isMobile) {
          setImportant(message, 'font-size', '13px');
          setImportant(message, 'line-height', '16px');
        } else {
          setImportant(message, 'font-size', '13px');
          setImportant(message, 'line-height', '1.35');
          setImportant(message, 'padding', '0 16px');
          setImportant(message, 'margin-bottom', '20px');
        }

        message.querySelectorAll('*').forEach((node) => {
          setImportant(node, 'font-weight', '600');
        });

        message.querySelectorAll(':scope > *, p, span').forEach((node) => {
          setImportant(node, 'margin-top', '0');
          setImportant(node, 'margin-bottom', '0');
          setImportant(node, 'align-self', 'center');
        });
      }
    }

    if (wrapper) {
      setImportant(wrapper, 'width', '100%');
      // Alto = sus filas (cada fila ya tiene altura fija abajo). Un alto fijo recortaba las guías con más filas.
      setImportant(wrapper, 'height', 'auto');
      setImportant(wrapper, 'min-height', '0');
      setImportant(wrapper, 'max-height', 'none');
      setImportant(wrapper, 'box-sizing', 'border-box');

      if (isMobile) {
        setImportant(wrapper, 'overflow', 'hidden');
      } else {
        setImportant(wrapper, 'overflow', 'hidden');
      }

      if (isMobile) {
        setImportant(wrapper, 'border', '1px solid rgba(255, 255, 255, 0.085)');
        setImportant(wrapper, 'outline', '0');
        setImportant(wrapper, 'box-shadow', 'none');
      }
    }

    if (table) {
      normalizeTable(table, isMobile);

      const rows = table.querySelectorAll('.ks-table-row, tr');
      const alturaFila = (index) => (index === 0 ? 38 : index === 1 ? (isMobile ? 40 : 42) : isMobile ? 43 : 44);
      // La altura va en cada CELDA, en px: Safari (WebKit) ignora la altura de las filas (<tr>), deja cada fila a la
      // altura de su texto y reparte el sobrante de un alto fijo de tabla en una sola fila (antes «MEN», luego la
      // cabecera). Con las celdas en px, la tabla mide exactamente la suma de sus filas en todos los navegadores.
      setImportant(table, 'height', 'auto');
      setImportant(table, 'min-height', '0');
      setImportant(table, 'max-height', 'none');
      if (wrapper) {
        setImportant(wrapper, 'height', 'auto');
        setImportant(wrapper, 'min-height', '0');
        setImportant(wrapper, 'max-height', 'none');
      }

      rows.forEach((row, index) => {
          const rowHeight = `${alturaFila(index)}px`;
          setImportant(row, 'height', rowHeight);
          setImportant(row, 'min-height', rowHeight);
          setImportant(row, 'max-height', rowHeight);

        row.querySelectorAll('.ks-table-cell, th, td, :scope > *').forEach((cell) => {
          setImportant(cell, 'height', rowHeight);
          setImportant(cell, 'min-height', rowHeight);
          setImportant(cell, 'max-height', rowHeight);
          setImportant(cell, 'box-sizing', 'border-box');
          setImportant(cell, 'padding-top', '0');
          setImportant(cell, 'padding-bottom', '0');
          setImportant(cell, 'vertical-align', 'middle');
        });
      });
    }
  };

  const updateAll = () => {
    document.querySelectorAll(messageSelector).forEach(updateMessage);
    document.querySelectorAll(modalSelector).forEach(normalizeModal);
  };

  const scheduleChecks = () => {
    window.clearTimeout(window.cwKiwiMessageCheck1);
    window.clearTimeout(window.cwKiwiMessageCheck2);
    window.clearTimeout(window.cwKiwiMessageCheck3);
    window.clearTimeout(window.cwKiwiMessageCheck4);

    window.cwKiwiMessageCheck1 = window.setTimeout(updateAll, 0);
    window.cwKiwiMessageCheck2 = window.setTimeout(updateAll, 150);
    window.cwKiwiMessageCheck3 = window.setTimeout(updateAll, 500);
    window.cwKiwiMessageCheck4 = window.setTimeout(updateAll, 1000);
  };

  window.cwKiwiMessageObserver?.disconnect();
  window.cwKiwiMessageObserver = new MutationObserver(updateAll);
  window.cwKiwiMessageObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });

  updateAll();

  document.addEventListener('click', (event) => {
    if (!event.target.closest('#KiwiSizingChart, .kiwi-sizing-modal')) return;
    scheduleChecks();
  }, true);

  window.addEventListener('resize', scheduleChecks, { passive: true });
  window.addEventListener('orientationchange', scheduleChecks, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleChecks, { passive: true });

  const clearColumnFix = (table) => {
    table.querySelectorAll(`[${fixedAttribute}]`).forEach((cell) => {
      cell.style.removeProperty('background');
      cell.style.removeProperty('background-color');
      cell.style.removeProperty('color');
      cell.removeAttribute(fixedAttribute);
    });
  };

  const isOpaqueWhite = (color) => {
    const values = color.match(/[\d.]+/g)?.map(Number);
    if (!values || values.length < 3) return false;

    const [red, green, blue, alpha = 1] = values;
    return alpha >= 0.65 && red >= 225 && green >= 225 && blue >= 225;
  };

  const softenColumnHover = (table) => {
    table.querySelectorAll(cellSelector).forEach((cell) => {
      if (!isOpaqueWhite(getComputedStyle(cell).backgroundColor)) return;

      cell.setAttribute(fixedAttribute, '');
      cell.style.setProperty('background-color', 'rgba(255, 255, 255, 0.12)', 'important');
      cell.style.setProperty('color', 'rgba(255, 255, 255, 0.96)', 'important');
    });
  };

  // Resaltado legible también al TOCAR (iPhone): el toque dispara mouseover y deja la fila y la columna con el
  // gris casi blanco de Kiwi y el texto blanco (ilegible). Antes solo se suavizaba en dispositivos con ratón.
  // Se repasa tras el siguiente frame y otra vez un poco después, porque Safari aplica :hover tras el evento.
  const suavizarTrasToque = (table) => {
    clearColumnFix(table);
    window.requestAnimationFrame(() => softenColumnHover(table));
    window.setTimeout(() => softenColumnHover(table), 120);
  };

  document.addEventListener('mouseover', (event) => {
    const table = event.target.closest(`${modalSelector} ${tableSelector}`);
    if (!table) return;

    suavizarTrasToque(table);
  }, true);

  document.addEventListener('click', (event) => {
    const table = event.target.closest(`${modalSelector} ${tableSelector}`);
    if (table) suavizarTrasToque(table);
  }, true);

  document.addEventListener('mouseout', (event) => {
    const table = event.target.closest(`${modalSelector} ${tableSelector}`);
    if (!table || table.contains(event.relatedTarget)) return;

    clearColumnFix(table);
  }, true);
})();
